/**
 * Pergunte ao Cockpit: o WhatsBI Cockpit na tela (ADR-043).
 *
 * O mesmo agente que responde pelo WhatsApp da concessionária. A resposta chega da API em
 * duas partes, e a tela mostra as duas:
 *
 * - **o texto**, que o modelo de linguagem redigiu — já conferido no servidor: todo número
 *   dele está numa consulta. Sem modelo, ou quando a conferência o derrubou, não há texto;
 * - **as fichas**, cada uma uma **célula de notebook** (`CelulaDoAgente`, `W-14`): o
 *   gráfico desenhado aqui do dado da consulta, a tabela ordenável que baixa em planilha, as
 *   linhas, o "como calculei" e o link que abre o conjunto contado.
 *
 * E, embaixo da última resposta, **as continuações** (`W-12`): "e em agosto?", "e por
 * praça?" — perguntas que o servidor escreve no vocabulário das regras, para que tocar numa
 * nunca caia em "não entendi", com ou sem modelo.
 *
 * A tela não calcula nada, e diz **quem entendeu a pergunta** — o modelo ou as regras —,
 * porque os dois erram de jeitos diferentes e quem lê precisa saber qual leu.
 *
 * **A conversa fica** (`W-15`, ADR-050): abrir o painel reabre a última conversa da tela, como
 * ela foi — as respostas vêm do registro, com os gráficos e as tabelas. "Conversas" lista as
 * anteriores (as do WhatsApp também, para ler); "Nova conversa" limpa a tela e começa outra.
 *
 * E, para quem tem o celular cadastrado, **os alertas pelo WhatsApp** (`W-08`,
 * `AlertasNoWhatsApp`): a pessoa liga os dela aqui.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ConversaInteira,
  ConversaNaLista,
  MeuWhatsApp,
  MinhasConversas,
  RespostaDoAgente,
  Sugestoes,
} from "../api/tipos";
import { dataCurta } from "../formato";
import { AlertasNoWhatsApp } from "./AlertasNoWhatsApp";
import { Badge, Botao } from "./base";
import { RespostaNaTela } from "./CelulaDoAgente";
import { type Turno, turnosDaConversa } from "./conversa";
import { Icone } from "./icones";

const LIMITE = 500;

export function PergunteAoCockpit({ aoFechar }: { aoFechar: () => void }) {
  const [turnos, definirTurnos] = useState<Turno[]>([]);
  const [conversa, definirConversa] = useState<string | null>(null);
  // A conversa do WhatsApp aparece para ler; continua no WhatsApp.
  const [doWhatsapp, definirDoWhatsapp] = useState(false);
  const [texto, definirTexto] = useState("");
  const [enviando, definirEnviando] = useState(false);
  const [abrindo, definirAbrindo] = useState(true);
  const [vista, definirVista] = useState<"conversa" | "lista">("conversa");
  const [lista, definirLista] = useState<ConversaNaLista[] | null>(null);
  const [maisNaLista, definirMaisNaLista] = useState(false);
  const [sugestoes, definirSugestoes] = useState<string[]>([]);
  // Por quantos dias a conversa fica guardada (`W-09`): quem pergunta sabe antes de perguntar.
  const [guarda, definirGuarda] = useState<number | null>(null);
  const [whatsapp, definirWhatsapp] = useState<MeuWhatsApp | null>(null);
  const fim = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLTextAreaElement>(null);

  const abrirConversa = useCallback(async (id: string) => {
    definirAbrindo(true);
    try {
      const guardada = await api<ConversaInteira>(`/whatsbi/conversas/${id}`);
      definirTurnos(turnosDaConversa(guardada));
      definirConversa(guardada.id);
      definirDoWhatsapp(!guardada.pode_continuar);
      definirVista("conversa");
    } catch {
      // Sem a conversa (o prazo de guarda a levou, por exemplo), começa outra.
      definirTurnos([]);
      definirConversa(null);
      definirDoWhatsapp(false);
    } finally {
      definirAbrindo(false);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    api<Sugestoes>("/whatsbi/sugestoes")
      .then((s) => {
        if (!vivo) return;
        definirSugestoes(s.sugestoes);
        definirGuarda(s.guarda_dias ?? null);
      })
      .catch(() => undefined);
    api<MeuWhatsApp>("/whatsbi/meu-whatsapp")
      .then((w) => vivo && definirWhatsapp(w))
      .catch(() => undefined);
    // A última conversa da tela volta como estava (`W-15`).
    api<MinhasConversas>("/whatsbi/conversas?canal=cockpit")
      .then((minhas) => {
        if (!vivo) return;
        const ultima = minhas.conversas?.[0];
        if (ultima) void abrirConversa(ultima.id);
        else definirAbrindo(false);
      })
      .catch(() => vivo && definirAbrindo(false));
    campo.current?.focus();
    return () => {
      vivo = false;
    };
  }, [abrirConversa]);

  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: "end" });
  }, [turnos]);

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") aoFechar();
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);

  async function verLista(pagina = 0) {
    definirVista("lista");
    try {
      const minhas = await api<MinhasConversas>(
        `/whatsbi/conversas?pagina=${pagina}`,
      );
      definirLista((antes) =>
        pagina === 0
          ? (minhas.conversas ?? [])
          : [...(antes ?? []), ...(minhas.conversas ?? [])],
      );
      definirMaisNaLista(minhas.mais);
    } catch {
      definirLista((antes) => antes ?? []);
    }
  }

  async function perguntar(pergunta: string) {
    const limpa = pergunta.trim();
    if (!limpa || enviando || doWhatsapp) return;
    definirTexto("");
    definirEnviando(true);
    definirTurnos((t) => [...t, { pergunta: limpa }]);
    let fechado: Turno;
    try {
      const resposta = await api<RespostaDoAgente>("/whatsbi/conversar", {
        method: "POST",
        corpo: conversa
          ? { mensagem: limpa, conversa_id: conversa }
          : { mensagem: limpa },
      });
      definirConversa(resposta.conversa_id);
      fechado = { pergunta: limpa, resposta };
    } catch (falha) {
      // A cota do agente acabou (`B-03`): a pergunta e a frase ficaram numa conversa, e a
      // próxima pergunta continua nela.
      const daCota =
        falha instanceof ErroDaApi && falha.cotaEsgotada
          ? (falha.detalhe as { conversa_id?: string } | undefined)?.conversa_id
          : undefined;
      if (daCota) definirConversa(daCota);
      fechado = {
        pergunta: limpa,
        erro:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível perguntar agora.",
      };
    }
    definirTurnos((t) => [...t.slice(0, -1), fechado]);
    definirEnviando(false);
    campo.current?.focus();
  }

  function novaConversa() {
    definirTurnos([]);
    definirConversa(null);
    definirDoWhatsapp(false);
    definirVista("conversa");
    campo.current?.focus();
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault();
    void perguntar(texto);
  }

  return (
    <aside
      role="dialog"
      aria-label="Pergunte ao Cockpit"
      className="fixed inset-0 z-30 grid grid-rows-[auto_1fr_auto] bg-fundo md:inset-y-0 md:right-0 md:left-auto md:w-[480px] md:border-l md:border-linha md:shadow-2xl"
    >
      <header className="grid gap-1 border-b border-linha bg-topo px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-azul-claro">
            <Icone.Conversa />
          </span>
          <h2 className="min-w-0 truncate text-[13px] font-bold text-tinta">
            Pergunte ao Cockpit
          </h2>
          {/* Sem o selo "WhatsBI" aqui (07/10): o painel tem 480 px em qualquer tela, e com
              "Conversas", "Nova" e o fechar ele passava do limite e cortava o fechar. */}
          <span className="ml-auto flex shrink-0 gap-1.5">
            <Botao
              onClick={() =>
                vista === "lista" ? definirVista("conversa") : void verLista()
              }
              aria-pressed={vista === "lista"}
              aria-label="Conversas"
              title="As suas conversas anteriores"
            >
              <Icone.Lista />
              <span className="hidden sm:inline">Conversas</span>
            </Botao>
            {(turnos.length > 0 || vista === "lista") && (
              <Botao
                onClick={novaConversa}
                disabled={enviando}
                aria-label="Nova conversa"
                title="Limpa a tela e começa uma conversa nova"
              >
                <span aria-hidden="true">+</span>
                Nova
              </Botao>
            )}
            <Botao onClick={aoFechar} aria-label="Fechar" title="Fechar (Esc)">
              <Icone.Fechar />
            </Botao>
          </span>
        </div>
        {/* No celular, o aviso sai de cena quando a conversa começa: são três linhas de
            uma tela que já é pequena, e quem perguntou já o leu. */}
        <p
          className={`text-[11px] text-tinta-3 ${turnos.length > 0 ? "hidden md:block" : ""}`}
        >
          Os números saem das mesmas consultas das telas. O modelo de linguagem
          recebe a pergunta e os totais das consultas — nunca placa, imagem ou
          dado de pessoa.
          {guarda !== null &&
            ` A conversa fica guardada por ${guarda} dias — você a reabre em Conversas, e o administrador da organização também pode lê-la — e depois sai do banco.`}
        </p>
        {whatsapp?.disponivel && whatsapp.numero_do_cockpit && (
          <p className="text-[11px] text-verde">
            {whatsapp.meu_numero
              ? `Pergunte também pelo WhatsApp: ${whatsapp.numero_do_cockpit}`
              : `O WhatsApp da organização é ${whatsapp.numero_do_cockpit}; peça ao administrador para cadastrar o seu celular.`}
          </p>
        )}
        {/* Os alertas (`W-08`): só para quem tem o celular cadastrado. Fechado, é uma linha —
            fica também no celular, onde o painel reabre a última conversa e o aviso de
            cima sai de cena: escondido ali, quem tem conversa nunca o acharia. */}
        {whatsapp?.disponivel && whatsapp.meu_numero && <AlertasNoWhatsApp />}
      </header>

      {vista === "lista" ? (
        <ListaDeConversas
          conversas={lista}
          atual={conversa}
          mais={maisNaLista}
          aoAbrir={(id) => void abrirConversa(id)}
          aoVerMais={() => void verLista(Math.ceil((lista?.length ?? 0) / 30))}
        />
      ) : (
        <div
          className="grid min-w-0 grid-cols-1 content-start gap-3 overflow-y-auto px-3 py-3"
          aria-live="polite"
          aria-busy={abrindo}
        >
          {abrindo && turnos.length === 0 && (
            <p className="text-[11.5px] text-tinta-3">Abrindo a conversa…</p>
          )}
          {!abrindo && turnos.length === 0 && (
            <div className="grid gap-2">
              <p className="text-[12px] text-tinta-2">
                Pergunte como anda a operação: passagens, perdas e por quê,
                envios à SENATRAN, isentos, arrecadação, o prazo de 24 h, a
                previsão do trimestre ou a perda com chuva — ou qualquer recorte
                deles: por hora, por dia, mês a mês, por praça, por dia da
                semana. A resposta vem com gráfico e tabela. Dá para continuar o
                assunto — “e por praça?”. Por exemplo:
              </p>
              <ul className="flex flex-wrap gap-1.5" aria-label="Sugestões">
                {sugestoes.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => void perguntar(s)}
                      className="rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-left text-[11.5px] text-tinta-2 hover:bg-realce"
                    >
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {turnos.map((t, i) => (
            <div key={i} className="grid min-w-0 grid-cols-1 gap-1.5">
              <p className="max-w-[85%] justify-self-end rounded-cartao border border-azul-borda bg-azul-fundo px-2.5 py-1.5 text-[12px] text-tinta">
                {t.pergunta}
              </p>
              {t.resposta ? (
                <RespostaNaTela
                  resposta={t.resposta}
                  aoAbrir={aoFechar}
                  aoContinuar={
                    i === turnos.length - 1 && !enviando && !doWhatsapp
                      ? (p) => void perguntar(p)
                      : undefined
                  }
                />
              ) : t.erro ? (
                <p
                  role="alert"
                  className="rounded-cartao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-1.5 text-[12px] text-vermelho"
                >
                  {t.erro}
                </p>
              ) : (
                <p className="text-[11.5px] text-tinta-3">Consultando…</p>
              )}
            </div>
          ))}
          <div ref={fim} />
        </div>
      )}

      {doWhatsapp ? (
        <p className="border-t border-linha bg-topo px-3 py-3 text-[11.5px] text-tinta-2">
          Esta conversa foi pelo WhatsApp: continue por lá — ou comece uma{" "}
          <button
            type="button"
            onClick={novaConversa}
            className="font-semibold text-azul-claro hover:underline"
          >
            nova conversa aqui
          </button>
          .
        </p>
      ) : (
        <form
          onSubmit={aoEnviar}
          className="grid gap-1.5 border-t border-linha bg-topo px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]"
        >
          <label className="sr-only" htmlFor="pergunta-ao-cockpit">
            Sua pergunta
          </label>
          <textarea
            id="pergunta-ao-cockpit"
            ref={campo}
            rows={2}
            maxLength={LIMITE}
            value={texto}
            onChange={(e) => definirTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void perguntar(texto);
              }
            }}
            placeholder="Ex.: passagens por hora e por praça nesta semana"
            className="w-full resize-none rounded-botao border border-linha bg-painel-2 px-2.5 py-1.5 text-[12.5px] text-tinta placeholder:text-tinta-3 focus:border-azul"
          />
          <div className="flex items-center gap-2">
            <span className="hidden text-[10.5px] text-tinta-3 md:inline">
              Enter envia · Shift+Enter quebra a linha
            </span>
            <span className="ml-auto">
              <Botao
                papel="primaria"
                type="submit"
                disabled={enviando || !texto.trim()}
              >
                <Icone.Enviar />
                {enviando ? "Consultando…" : "Perguntar"}
              </Botao>
            </span>
          </div>
        </form>
      )}
    </aside>
  );
}

function ListaDeConversas({
  conversas,
  atual,
  mais,
  aoAbrir,
  aoVerMais,
}: {
  conversas: ConversaNaLista[] | null;
  atual: string | null;
  mais: boolean;
  aoAbrir: (id: string) => void;
  aoVerMais: () => void;
}) {
  return (
    <nav
      aria-label="Suas conversas"
      className="grid content-start gap-1.5 overflow-y-auto px-3 py-3"
    >
      <h3 className="text-[11px] font-bold tracking-wide text-tinta-3 uppercase">
        Suas conversas
      </h3>
      {conversas === null ? (
        <p className="text-[11.5px] text-tinta-3">Carregando…</p>
      ) : conversas.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">
          Nenhuma conversa ainda. As que você fizer aqui e pelo WhatsApp
          aparecem nesta lista.
        </p>
      ) : (
        <ul className="grid gap-1">
          {conversas.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => aoAbrir(c.id)}
                aria-current={c.id === atual ? "true" : undefined}
                className={`grid w-full gap-0.5 rounded-botao border px-2.5 py-2 text-left hover:bg-realce ${
                  c.id === atual
                    ? "border-azul-borda bg-azul-fundo"
                    : "border-linha bg-painel-2"
                }`}
              >
                <span className="line-clamp-2 text-[12px] text-tinta">
                  {c.primeira_pergunta}
                </span>
                <span className="flex items-center gap-1.5 text-[10.5px] text-tinta-3">
                  {dataCurta(c.ultima_em)} ·{" "}
                  {c.perguntas === 1
                    ? "1 pergunta"
                    : `${c.perguntas} perguntas`}
                  {c.canal === "whatsapp" && (
                    <Badge cor="verde">WhatsApp</Badge>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {mais && <Botao onClick={aoVerMais}>Ver conversas mais antigas</Botao>}
    </nav>
  );
}
